import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../helpers/Supabase';
import Loader from './Loader/Loader';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const history = useHistory();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (error || !user) {
          history.push('/');
          return;
        }

        // Check if user is a delegate
        const { data: clientDelegate } = await supabase
          .from('client_delegates')
          .select('*')
          .eq('email', user.email)
          .eq('is_active', true)
          .single();

        const { data: storeDelegate } = await supabase
          .from('store_delegates')
          .select('*')
          .eq('email', user.email)
          .eq('is_active', true)
          .single();

        if (clientDelegate || storeDelegate) {
          // User is a delegate with access
          setHasAccess(true);
        } else {
          // Check if user is a regular client user
          const { data: userRow } = await supabase
            .from('users')
            .select('client_id')
            .eq('auth_user_id', user.id)
            .single();

          if (userRow?.client_id) {
            // Regular client user
            setHasAccess(true);
          } else {
            // User exists but has no access
            history.push('/');
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        history.push('/');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [history]);

  if (isLoading) {
    return <Loader />;
  }

  return hasAccess ? <>{children}</> : null;
};

export default AuthGuard; 